import { FccAdAlertType } from '@backoffice-frontend/graphql'
import { useTheme } from '@backoffice-frontend/patterns'
import {
  MicrophoneIcon,
  MicrophoneSlashIcon,
  PhoneCallIcon,
  PhoneDownIcon,
} from '@moia-dev/pace-icons'
import { useTranslation } from 'react-i18next'
import { CallButton } from '../CallButton/CallButton'
import { useCall } from '../CallProvider/CallProvider'
import { CallControlButton } from './CallControlButton'
import type { CallControlsVehicleFragment } from './CallControls.hook'
import { ElapsedDuration } from './ElapsedDuration'
import { MicrophoneLevelWidget } from './MicrophoneLevelWidget'
import { RingingButton } from './RingingButton'

export const CallControls = ({
  vehicle,
}: {
  vehicle: CallControlsVehicleFragment
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { pickup, hangup, toggleMute, state, callAlertCreatedAt } = useCall()

  const incomingCallAlertType = vehicle.fccCurrentAdAlerts?.alerts.find(
    alert =>
      alert.type === FccAdAlertType.IncomingPassengerCall ||
      alert.type === FccAdAlertType.IncomingSafetyDriverCall,
  )?.type

  const outgoingCallAlertType = vehicle.fccCurrentAdAlerts?.alerts.find(
    alert =>
      alert.type === FccAdAlertType.OutgoingOperatorCall ||
      alert.type === FccAdAlertType.OutgoingOperatorToSafetyDriverCall,
  )?.type

  const getWaitingForAttendeeText = (alertType: FccAdAlertType) => {
    switch (alertType) {
      case FccAdAlertType.IncomingPassengerCall:
      case FccAdAlertType.IncomingSafetyDriverCall:
        return t('adFleetMap_callStatus_youAreBeingConnected_message')
      case FccAdAlertType.OutgoingOperatorCall:
        return t('adFleetMap_callStatus_passengerIsBeingConnected_message')
      case FccAdAlertType.OutgoingOperatorToSafetyDriverCall:
        return t('adFleetMap_callStatus_safetyDriverIsBeingConnected_message')
      default:
        return t('adFleetMap_callStatus_connecting_message')
    }
  }

  return (
    <div
      css={{
        display: 'flex',
        padding: `0 ${theme.space.Space6}`,
        gap: theme.space.Space4,
        alignItems: 'center',
        height: '100%',
      }}
    >
      {state.matches('idle') && <CallButton vehicle={vehicle} />}

      {incomingCallAlertType && (
        <RingingButton
          pickup={pickup}
          isRinging={state.matches('ringing')}
          alertType={incomingCallAlertType}
        />
      )}

      {(state.matches('requestingChimeCredentials') ||
        state.matches('initatingChimeMeeting') ||
        state.matches('waitingForAlertCreation') ||
        state.matches('joiningIncomingCall') ||
        state.matches('joiningOutgoingCall')) && (
        <>
          <CallControlButton
            icon={
              <PhoneCallIcon
                css={{
                  color: theme.semantic.ColorBackgroundAlternative,
                }}
              />
            }
            backgroundColor={theme.semantic.ColorActionSecondaryDisabled}
            disabled
          />
          {t('adFleetMap_callStatus_connecting_message')}
        </>
      )}

      {(state.matches('callInProgress') ||
        state.matches('waitingForCalleeToJoin')) && (
        <>
          <CallControlButton
            onClick={hangup}
            icon={
              <PhoneDownIcon
                css={{
                  color:
                    theme.semantic.ColorContentOnContrastCriticalSuccessDefault,
                }}
              />
            }
            backgroundColor={theme.semantic.ColorCriticalDefault}
          />
          <div css={{ minWidth: '44px' }}>
            <ElapsedDuration startTime={callAlertCreatedAt} />
          </div>
          <div css={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CallControlButton
              onClick={toggleMute}
              backgroundColor={
                state.context.isMuted
                  ? theme.semantic.ColorSurfaceReduced
                  : theme.semantic.ColorActionPrimaryAlternative
              }
              icon={
                state.context.isMuted ? (
                  <MicrophoneSlashIcon
                    css={{
                      color: theme.semantic.ColorContentDefault,
                      backgroundColor: theme.semantic.ColorSurfaceReduced,
                    }}
                  />
                ) : (
                  <MicrophoneIcon
                    css={{ color: theme.semantic.ColorBackgroundAlternative }}
                  />
                )
              }
            />
            <MicrophoneLevelWidget micMuted={state.context.isMuted} />
          </div>
          {state.matches('waitingForCalleeToJoin') && outgoingCallAlertType
            ? getWaitingForAttendeeText(outgoingCallAlertType)
            : null}
        </>
      )}

      {state.matches('waitingForAlertResolution') && (
        <>
          <CallControlButton
            icon={
              <PhoneCallIcon
                css={{
                  color: theme.semantic.ColorBackgroundAlternative,
                }}
              />
            }
            backgroundColor={theme.semantic.ColorActionSecondaryDisabled}
            disabled
          />
          {t('adFleetMap_callStatus_leavingCall_message')}
        </>
      )}
    </div>
  )
}
